import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Target, Shield, Brain, BarChart3, Network, TrendingUp, DollarSign, Calendar, FolderKanban, FileText, Eye } from 'lucide-react';
import { livrablesTangibles, type LivrableTangible } from '../data/livrablesTangibles';
import PlanDirecteurPresentation from './PlanDirecteurPresentation';
import NISTPresentation from './NISTPresentation';
import AIEthiquePresentation from './AIEthiquePresentation';
import DashboardClientComplet from './DashboardClientComplet';
import CatalogueActifsTI from './CatalogueActifsTI';
import VeilleTechnologiqueAI from './VeilleTechnologiqueAI';
import BudgetTIOptimise from './BudgetTIOptimise';
import ComiteTI from './ComiteTI';
import ChargeProjetTactiCe from './ChargeProjetTactiCe';

const iconMap: Record<string, any> = {
  Target, Shield, Brain, BarChart3, Network, TrendingUp, DollarSign, Calendar, FolderKanban, FileText, Eye
};

interface LivrablesPortfolioProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LivrablesPortfolio({ isOpen, onClose }: LivrablesPortfolioProps) {
  const [selectedLivrable, setSelectedLivrable] = useState<LivrableTangible | null>(null);
  const [isPlanDirecteurOpen, setIsPlanDirecteurOpen] = useState(false);
  const [isNISTOpen, setIsNISTOpen] = useState(false);
  const [isAIEthiqueOpen, setIsAIEthiqueOpen] = useState(false);
  const [isDashboardClientOpen, setIsDashboardClientOpen] = useState(false);
  const [isCatalogueActifsOpen, setIsCatalogueActifsOpen] = useState(false);
  const [isVeilleAIOpen, setIsVeilleAIOpen] = useState(false);
  const [isBudgetTIOpen, setIsBudgetTIOpen] = useState(false);
  const [isComiteTIOpen, setIsComiteTIOpen] = useState(false);
  const [isChargeProjetOpen, setIsChargeProjetOpen] = useState(false);

  const handleLivrableClick = (livrable: LivrableTangible) => {
    if (livrable.id === 'plan-directeur') {
      setIsPlanDirecteurOpen(true);
    } else if (livrable.id === 'cadre-nist') {
      setIsNISTOpen(true);
    } else if (livrable.id === 'ia-ethique') {
      setIsAIEthiqueOpen(true);
    } else if (livrable.id === 'dashboard-client') {
      setIsDashboardClientOpen(true);
    } else if (livrable.id === 'catalogue-actifs-ti') {
      setIsCatalogueActifsOpen(true);
    } else if (livrable.id === 'veille-technologique-ai') {
      setIsVeilleAIOpen(true);
    } else if (livrable.id === 'budget-ti-optimise') {
      setIsBudgetTIOpen(true);
    } else if (livrable.id === 'comite-ti') {
      setIsComiteTIOpen(true);
    } else if (livrable.id === 'charge-projet-outils') {
      setIsChargeProjetOpen(true);
    } else {
      setSelectedLivrable(livrable);
    }
  };

  const renderContent = (livrable: LivrableTangible) => {
    switch (livrable.content.type) {
      case 'presentation':
        return (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 text-center">
            <div className="text-4xl mb-4">🎯</div>
            <h4 className="text-xl font-bold text-bz-dark mb-2">Présentation Interactive</h4>
            <p className="text-gray-600 mb-4">
              Cliquez pour ouvrir la présentation complète avec slides interactives
            </p>
            <button
              onClick={() => {
                if (livrable.id === 'plan-directeur') {
                  setIsPlanDirecteurOpen(true);
                } else if (livrable.id === 'cadre-nist') {
                  setIsNISTOpen(true);
                } else if (livrable.id === 'ia-ethique') {
                  setIsAIEthiqueOpen(true);
                } else if (livrable.id === 'dashboard-client') {
                  setIsDashboardClientOpen(true);
                } else if (livrable.id === 'catalogue-actifs-ti') {
                  setIsCatalogueActifsOpen(true);
                } else if (livrable.id === 'veille-technologique-ai') {
                  setIsVeilleAIOpen(true);
                } else if (livrable.id === 'budget-ti-optimise') {
                  setIsBudgetTIOpen(true);
                } else if (livrable.id === 'comite-ti') {
                  setIsComiteTIOpen(true);
                } else if (livrable.id === 'charge-projet-outils') {
                  setIsChargeProjetOpen(true);
                }
              }}
              className="bg-bz-blue hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Ouvrir la Présentation
            </button>
          </div>
        );
      case 'document':
        return (
          <div className="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
              {livrable.content.template}
            </pre>
          </div>
        );
      
      case 'spider':
        return (
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-800 mb-4">Graphique Spider - {livrable.title}</h4>
            <div className="aspect-square max-w-md mx-auto bg-gradient-to-br from-red-100 to-orange-100 rounded-lg flex items-center justify-center">
              <div className="text-center p-8">
                <div className="text-4xl mb-4">🕸️</div>
                <div className="text-gray-700 font-medium">Spider Chart NIST</div>
                <div className="text-sm text-gray-500 mt-2">
                  6 fonctions: {livrable.content.data?.categories.join(', ')}
                </div>
                <div className="mt-4 text-xs text-gray-600">
                  ASIS: {livrable.content.data?.asis.join(', ')}<br/>
                  Target: {livrable.content.data?.target.join(', ')}
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'dashboard':
        const data = livrable.content.data;
        return (
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-800 mb-4">Dashboard Client - {data?.client?.name}</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-3 rounded border">
                <div className="text-xs text-gray-500">Chiffre d'affaires</div>
                <div className="text-lg font-bold text-green-600">{data?.financial?.ca}M$</div>
              </div>
              <div className="bg-white p-3 rounded border">
                <div className="text-xs text-gray-500">Profit</div>
                <div className="text-lg font-bold text-blue-600">{data?.financial?.profit}M$</div>
              </div>
              <div className="bg-white p-3 rounded border">
                <div className="text-xs text-gray-500">Budget TI</div>
                <div className="text-lg font-bold text-purple-600">{data?.financial?.budget_ti}M$</div>
              </div>
              <div className="bg-white p-3 rounded border">
                <div className="text-xs text-gray-500">ROI</div>
                <div className="text-lg font-bold text-orange-600">{data?.financial?.roi}%</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded border">
                <h5 className="font-medium mb-2">Projets en cours</h5>
                {data?.projects?.map((project: any, idx: number) => (
                  <div key={idx} className="flex justify-between items-center text-sm mb-1">
                    <span>{project.name}</span>
                    <span className="text-gray-500">{project.progress}%</span>
                  </div>
                ))}
              </div>
              <div className="bg-white p-4 rounded border">
                <h5 className="font-medium mb-2">Roadmap 2024</h5>
                {Object.entries(data?.roadmap || {}).map(([quarter, content]: [string, any]) => (
                  <div key={quarter} className="text-sm mb-1">
                    <span className="font-medium">{quarter.toUpperCase()}:</span> {content}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      case 'framework':
        return (
          <div className="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
              {livrable.content.template}
            </pre>
          </div>
        );
      
      case 'topology':
        return (
          <div className="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
              {livrable.content.template}
            </pre>
          </div>
        );
      
      default:
        return <div className="text-gray-500">Contenu non disponible</div>;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-bz-dark to-bz-blue text-white p-6 rounded-t-xl z-10">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-3xl font-bold mb-2">📁 Portfolio de Livrables Tangibles</h2>
                    <p className="text-white/90">Exemples concrets pour la communication client haut de gamme</p>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Grille des livrables */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {livrablesTangibles.map((livrable, index) => {
                    const Icon = iconMap[livrable.icon] || FileText;
                    return (
                      <motion.div
                        key={livrable.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleLivrableClick(livrable)}
                        className="bg-white border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg hover:border-bz-blue transition-all group"
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className="p-3 rounded-lg"
                            style={{ backgroundColor: `${livrable.color}20` }}
                          >
                            <Icon className="w-6 h-6" style={{ color: livrable.color }} />
                          </div>
                          
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-800 group-hover:text-bz-blue transition-colors mb-1">
                              {livrable.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2">{livrable.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                {livrable.category}
                              </span>
                              <Eye className="w-4 h-4 text-gray-400 group-hover:text-bz-blue transition-colors" />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl flex justify-between items-center border-t">
                <div className="text-sm text-gray-600">
                  <strong>Note:</strong> Ces livrables démontrent la valeur ajoutée BZ et facilitent la communication client
                </div>
                <button
                  onClick={onClose}
                  className="aikup-button"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>

          {/* Modal de détail du livrable */}
          <AnimatePresence>
            {selectedLivrable && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                onClick={() => setSelectedLivrable(null)}
              >
                <div className="absolute inset-0 bg-black/60" />
                <motion.div
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto"
                >
                  <div
                    className="p-6 text-white rounded-t-xl"
                    style={{ backgroundColor: selectedLivrable.color }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-2xl font-bold mb-2">{selectedLivrable.title}</h3>
                        <p className="text-white/90">{selectedLivrable.description}</p>
                      </div>
                      <button
                        onClick={() => setSelectedLivrable(null)}
                        className="text-white hover:bg-white/10 rounded-full p-2"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    {renderContent(selectedLivrable)}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Plan Directeur Presentation */}
          <PlanDirecteurPresentation
            isOpen={isPlanDirecteurOpen}
            onClose={() => setIsPlanDirecteurOpen(false)}
          />

          {/* NIST Presentation */}
          <NISTPresentation
            isOpen={isNISTOpen}
            onClose={() => setIsNISTOpen(false)}
          />

          {/* AI Ethique Presentation */}
          <AIEthiquePresentation
            isOpen={isAIEthiqueOpen}
            onClose={() => setIsAIEthiqueOpen(false)}
          />

          {/* Dashboard Client Complet */}
          <DashboardClientComplet
            isOpen={isDashboardClientOpen}
            onClose={() => setIsDashboardClientOpen(false)}
          />

          {/* Catalogue Actifs TI */}
          <CatalogueActifsTI
            isOpen={isCatalogueActifsOpen}
            onClose={() => setIsCatalogueActifsOpen(false)}
          />

          {/* Veille Technologique IA */}
          <VeilleTechnologiqueAI
            isOpen={isVeilleAIOpen}
            onClose={() => setIsVeilleAIOpen(false)}
          />

          {/* Budget TI Optimisé */}
          <BudgetTIOptimise
            isOpen={isBudgetTIOpen}
            onClose={() => setIsBudgetTIOpen(false)}
          />

          {/* Comité TI */}
          <ComiteTI
            isOpen={isComiteTIOpen}
            onClose={() => setIsComiteTIOpen(false)}
          />

          {/* Charge de Projet TactiCe */}
          <ChargeProjetTactiCe
            isOpen={isChargeProjetOpen}
            onClose={() => setIsChargeProjetOpen(false)}
          />
        </>
      )}
    </AnimatePresence>
  );
}
